/**
 * React hooks for working with Result<T, E> pattern from services.
 *
 * These hooks bridge the gap between TanStack Query and the Result pattern,
 * providing a unified API for handling async operations with type-safe errors.
 *
 * Design invariants:
 * - Result is always { success: true, data: T } | { success: false, error: E }
 * - Hooks never throw - all errors are captured in the Result
 * - Type guards enable compile-time error discrimination
 * - Callbacks are optional and fire on state transitions
 *
 * @example
 * ```typescript
 * // Using useAsyncResult with a service call
 * const { data, error, isLoading, execute, retry } = useAsyncResult(
 *   async () => modelsService.list(),
 *   { executeOnMount: true }
 * )
 *
 * if (isLoading) return <Spinner />
 * if (error) return <ErrorAlert error={error} onRetry={retry} />
 * return <ModelList models={data.models} />
 * ```
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import type { APIErrorResponse } from '@/types/errors'
import { isRetryableError, getErrorMessage, categorizeAPIError } from '@/lib/error-utils'

// ============================================================================
// Types
// ============================================================================

/**
 * Result type matching the service layer pattern.
 * Re-exported here for convenience.
 */
export type Result<T, E = APIErrorResponse> =
  | { success: true; data: T }
  | { success: false; error: E }

/**
 * State for async Result operations.
 */
export type AsyncResultState<T, E = APIErrorResponse> = {
  /** The successful data, undefined until success */
  data: T | undefined
  /** The error, undefined until failure */
  error: E | undefined
  /** True while the async operation is in progress */
  isLoading: boolean
  /** True if the last operation succeeded */
  isSuccess: boolean
  /** True if the last operation failed */
  isError: boolean
  /** True if the operation has never been executed */
  isIdle: boolean
}

/**
 * Options for useAsyncResult hook.
 */
export type UseAsyncResultOptions<T, E = APIErrorResponse> = {
  /** Execute the operation immediately on mount */
  executeOnMount?: boolean
  /** Callback fired on successful result */
  onSuccess?: (data: T) => void
  /** Callback fired on error result */
  onError?: (error: E) => void
  /** Callback fired when operation settles (success or error) */
  onSettled?: () => void
  /** Number of automatic retries for retryable errors (default: 0) */
  retryCount?: number
  /** Delay between retries in ms (default: 1000) */
  retryDelay?: number
  /** Initial data to use before first fetch */
  initialData?: T
}

/**
 * Return type for useAsyncResult hook.
 */
export type UseAsyncResultReturn<T, E = APIErrorResponse> = AsyncResultState<T, E> & {
  /** Execute the async operation */
  execute: () => Promise<Result<T, E>>
  /** Retry the last operation (only available after error) */
  retry: () => Promise<Result<T, E>>
  /** Reset state to idle */
  reset: () => void
  /** Category info for the current error */
  errorCategory: ReturnType<typeof categorizeAPIError> | undefined
  /** Human-readable error message */
  errorMessage: string | undefined
  /** Whether the current error is retryable */
  isRetryable: boolean
}

/**
 * Options for useResult hook (synchronous Result handling).
 */
export type UseResultOptions<T, E = APIErrorResponse> = {
  /** Callback fired when result is successful */
  onSuccess?: (data: T) => void
  /** Callback fired when result is an error */
  onError?: (error: E) => void
}

/**
 * Return type for useResult hook.
 */
export type UseResultReturn<T, E = APIErrorResponse> = {
  /** The successful data, undefined if not success */
  data: T | undefined
  /** The error, undefined if not error */
  error: E | undefined
  /** True if result is successful */
  isSuccess: boolean
  /** True if result is an error */
  isError: boolean
  /** Category info for the error */
  errorCategory: ReturnType<typeof categorizeAPIError> | undefined
  /** Human-readable error message */
  errorMessage: string | undefined
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for handling synchronous Result values with side effects.
 *
 * Use this when you already have a Result and want to:
 * - Trigger callbacks on success/error
 * - Extract data/error with proper typing
 * - Get error categorization info
 *
 * @example
 * ```typescript
 * const result = await modelsService.get(modelId)
 * const { data, error, isSuccess, errorMessage } = useResult(result, {
 *   onSuccess: (model) => console.log('Loaded:', model.name),
 *   onError: (err) => toast.error(err.message)
 * })
 * ```
 */
export function useResult<T, E = APIErrorResponse>(
  result: Result<T, E> | undefined,
  options: UseResultOptions<T, E> = {}
): UseResultReturn<T, E> {
  const { onSuccess, onError } = options
  const prevResultRef = useRef<Result<T, E> | undefined>(undefined)

  // Fire callbacks on result changes
  useEffect(() => {
    if (!result || result === prevResultRef.current) return
    prevResultRef.current = result

    if (result.success && onSuccess) {
      onSuccess(result.data)
    } else if (!result.success && onError) {
      onError(result.error)
    }
  }, [result, onSuccess, onError])

  if (!result) {
    return {
      data: undefined,
      error: undefined,
      isSuccess: false,
      isError: false,
      errorCategory: undefined,
      errorMessage: undefined,
    }
  }

  if (result.success) {
    return {
      data: result.data,
      error: undefined,
      isSuccess: true,
      isError: false,
      errorCategory: undefined,
      errorMessage: undefined,
    }
  }

  const errorCategory = categorizeAPIError(result.error)
  const errorMessage = getErrorMessage(result.error)

  return {
    data: undefined,
    error: result.error,
    isSuccess: false,
    isError: true,
    errorCategory,
    errorMessage,
  }
}

/**
 * Hook for executing async operations that return Result<T, E>.
 *
 * Provides a unified interface for:
 * - Loading/error/success state management
 * - Automatic retry for retryable errors
 * - Type-safe error handling with categorization
 * - Optional execution on mount
 *
 * @example
 * ```typescript
 * // Basic usage with executeOnMount
 * const models = useAsyncResult(
 *   () => modelsService.list(),
 *   { executeOnMount: true }
 * )
 *
 * // With callbacks
 * const deleteModel = useAsyncResult(
 *   () => modelsService.delete(modelId),
 *   {
 *     onSuccess: () => {
 *       toast.success('Model deleted')
 *       queryClient.invalidateQueries(['models'])
 *     },
 *     onError: (err) => toast.error(err.message)
 *   }
 * )
 *
 * // Manual execution (for mutations)
 * <Button onClick={() => deleteModel.execute()} disabled={deleteModel.isLoading}>
 *   {deleteModel.isLoading ? 'Deleting...' : 'Delete'}
 * </Button>
 * ```
 */
export function useAsyncResult<T, E = APIErrorResponse>(
  asyncFn: () => Promise<Result<T, E>>,
  options: UseAsyncResultOptions<T, E> = {}
): UseAsyncResultReturn<T, E> {
  const {
    executeOnMount = false,
    onSuccess,
    onError,
    onSettled,
    retryCount = 0,
    retryDelay = 1000,
    initialData,
  } = options

  const [state, setState] = useState<AsyncResultState<T, E>>({
    data: initialData,
    error: undefined,
    isLoading: false,
    isSuccess: initialData !== undefined,
    isError: false,
    isIdle: initialData === undefined,
  })

  // Track retry attempts
  const retryAttemptsRef = useRef(0)
  // Track if mounted
  const mountedRef = useRef(true)
  // Store the async function for retries
  const asyncFnRef = useRef(asyncFn)
  asyncFnRef.current = asyncFn

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const execute = useCallback(async (): Promise<Result<T, E>> => {
    if (!mountedRef.current) {
      return { success: false, error: { error_code: 'CANCELLED', message: 'Component unmounted' } as E }
    }

    setState(prev => ({
      ...prev,
      isLoading: true,
      isIdle: false,
    }))

    try {
      const result = await asyncFnRef.current()

      if (!mountedRef.current) {
        return result
      }

      if (result.success) {
        setState({
          data: result.data,
          error: undefined,
          isLoading: false,
          isSuccess: true,
          isError: false,
          isIdle: false,
        })
        onSuccess?.(result.data)
        retryAttemptsRef.current = 0
      } else {
        // Check if we should auto-retry
        const shouldRetry =
          retryAttemptsRef.current < retryCount &&
          isRetryableError(result.error)

        if (shouldRetry) {
          retryAttemptsRef.current++
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          return execute()
        }

        setState({
          data: undefined,
          error: result.error,
          isLoading: false,
          isSuccess: false,
          isError: true,
          isIdle: false,
        })
        onError?.(result.error)
        retryAttemptsRef.current = 0
      }

      onSettled?.()
      return result
    } catch (unexpectedError) {
      // This shouldn't happen if asyncFn follows the Result pattern,
      // but handle it gracefully
      const error = {
        error_code: 'UNEXPECTED_ERROR',
        message: unexpectedError instanceof Error ? unexpectedError.message : String(unexpectedError),
      } as E

      if (mountedRef.current) {
        setState({
          data: undefined,
          error,
          isLoading: false,
          isSuccess: false,
          isError: true,
          isIdle: false,
        })
        onError?.(error)
        onSettled?.()
      }

      return { success: false, error }
    }
  }, [onSuccess, onError, onSettled, retryCount, retryDelay])

  const retry = useCallback(async (): Promise<Result<T, E>> => {
    retryAttemptsRef.current = 0
    return execute()
  }, [execute])

  const reset = useCallback(() => {
    retryAttemptsRef.current = 0
    setState({
      data: initialData,
      error: undefined,
      isLoading: false,
      isSuccess: initialData !== undefined,
      isError: false,
      isIdle: initialData === undefined,
    })
  }, [initialData])

  // Execute on mount if requested
  useEffect(() => {
    if (executeOnMount) {
      execute()
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const errorCategory = state.error ? categorizeAPIError(state.error) : undefined
  const errorMessage = state.error ? getErrorMessage(state.error) : undefined
  const isRetryable = state.error ? isRetryableError(state.error) : false

  return {
    ...state,
    execute,
    retry,
    reset,
    errorCategory,
    errorMessage,
    isRetryable,
  }
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook for executing async Result operations with arguments.
 *
 * Similar to useAsyncResult but accepts a function that takes arguments.
 * Useful for mutations where you need to pass data.
 *
 * @example
 * ```typescript
 * const createModel = useAsyncResultFn(
 *   (name: string, useCase: string) => modelsService.create({ name, use_case: useCase }),
 *   {
 *     onSuccess: (model) => {
 *       toast.success(`Created ${model.name}`)
 *       navigate(`/models/${model.id}`)
 *     }
 *   }
 * )
 *
 * // Usage
 * <Button onClick={() => createModel.execute('My Model', 'churn_prediction')}>
 *   Create
 * </Button>
 * ```
 */
export function useAsyncResultFn<TArgs extends unknown[], T, E = APIErrorResponse>(
  asyncFn: (...args: TArgs) => Promise<Result<T, E>>,
  options: Omit<UseAsyncResultOptions<T, E>, 'executeOnMount'> = {}
): Omit<UseAsyncResultReturn<T, E>, 'execute'> & {
  execute: (...args: TArgs) => Promise<Result<T, E>>
} {
  const { onSuccess, onError, onSettled, retryCount = 0, retryDelay = 1000, initialData } = options

  const [state, setState] = useState<AsyncResultState<T, E>>({
    data: initialData,
    error: undefined,
    isLoading: false,
    isSuccess: initialData !== undefined,
    isError: false,
    isIdle: initialData === undefined,
  })

  const mountedRef = useRef(true)
  const lastArgsRef = useRef<TArgs | null>(null)
  const retryAttemptsRef = useRef(0)
  const asyncFnRef = useRef(asyncFn)
  asyncFnRef.current = asyncFn

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const execute = useCallback(async (...args: TArgs): Promise<Result<T, E>> => {
    lastArgsRef.current = args

    if (!mountedRef.current) {
      return { success: false, error: { error_code: 'CANCELLED', message: 'Component unmounted' } as E }
    }

    setState(prev => ({
      ...prev,
      isLoading: true,
      isIdle: false,
    }))

    try {
      const result = await asyncFnRef.current(...args)

      if (!mountedRef.current) {
        return result
      }

      if (result.success) {
        setState({
          data: result.data,
          error: undefined,
          isLoading: false,
          isSuccess: true,
          isError: false,
          isIdle: false,
        })
        onSuccess?.(result.data)
        retryAttemptsRef.current = 0
      } else {
        const shouldRetry =
          retryAttemptsRef.current < retryCount &&
          isRetryableError(result.error)

        if (shouldRetry) {
          retryAttemptsRef.current++
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          return execute(...args)
        }

        setState({
          data: undefined,
          error: result.error,
          isLoading: false,
          isSuccess: false,
          isError: true,
          isIdle: false,
        })
        onError?.(result.error)
        retryAttemptsRef.current = 0
      }

      onSettled?.()
      return result
    } catch (unexpectedError) {
      const error = {
        error_code: 'UNEXPECTED_ERROR',
        message: unexpectedError instanceof Error ? unexpectedError.message : String(unexpectedError),
      } as E

      if (mountedRef.current) {
        setState({
          data: undefined,
          error,
          isLoading: false,
          isSuccess: false,
          isError: true,
          isIdle: false,
        })
        onError?.(error)
        onSettled?.()
      }

      return { success: false, error }
    }
  }, [onSuccess, onError, onSettled, retryCount, retryDelay])

  const retry = useCallback(async (): Promise<Result<T, E>> => {
    if (!lastArgsRef.current) {
      return { success: false, error: { error_code: 'NO_PREVIOUS_CALL', message: 'No previous call to retry' } as E }
    }
    retryAttemptsRef.current = 0
    return execute(...lastArgsRef.current)
  }, [execute])

  const reset = useCallback(() => {
    retryAttemptsRef.current = 0
    lastArgsRef.current = null
    setState({
      data: initialData,
      error: undefined,
      isLoading: false,
      isSuccess: initialData !== undefined,
      isError: false,
      isIdle: initialData === undefined,
    })
  }, [initialData])

  const errorCategory = state.error ? categorizeAPIError(state.error) : undefined
  const errorMessage = state.error ? getErrorMessage(state.error) : undefined
  const isRetryable = state.error ? isRetryableError(state.error) : false

  return {
    ...state,
    execute,
    retry,
    reset,
    errorCategory,
    errorMessage,
    isRetryable,
  }
}

/**
 * Hook for combining multiple Result states into one.
 *
 * Useful when a component depends on multiple async operations.
 *
 * @example
 * ```typescript
 * const models = useAsyncResult(() => modelsService.list(), { executeOnMount: true })
 * const dataSources = useAsyncResult(() => dataSourcesService.list(), { executeOnMount: true })
 *
 * const combined = useCombinedResults([models, dataSources])
 *
 * if (combined.isLoading) return <Spinner />
 * if (combined.isError) return <ErrorAlert errors={combined.errors} />
 * // All data is now available
 * ```
 */
export function useCombinedResults<E = APIErrorResponse>(
  results: Array<AsyncResultState<unknown, E>>
): {
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  isIdle: boolean
  errors: E[]
} {
  const isLoading = results.some(r => r.isLoading)
  const isSuccess = results.every(r => r.isSuccess)
  const isError = results.some(r => r.isError)
  const isIdle = results.every(r => r.isIdle)
  const errors = results
    .filter(r => r.error !== undefined)
    .map(r => r.error as E)

  return {
    isLoading,
    isSuccess,
    isError,
    isIdle,
    errors,
  }
}

// ============================================================================
// TanStack Query Integration
// ============================================================================

/**
 * Return type from useQueryResult - enhances TanStack Query with Result pattern.
 */
export type QueryResultReturn<T, E = APIErrorResponse> = {
  /** The successful data, undefined until success */
  data: T | undefined
  /** The error from the Result, undefined if success */
  error: E | undefined
  /** True while query is loading */
  isLoading: boolean
  /** True if the last fetch succeeded */
  isSuccess: boolean
  /** True if the Result contained an error */
  isError: boolean
  /** Error category for displaying appropriate UI */
  errorCategory: ReturnType<typeof categorizeAPIError> | undefined
  /** Human-readable error message */
  errorMessage: string | undefined
  /** Whether error is retryable (server errors, rate limits) */
  isRetryable: boolean
  /** Refetch the query */
  refetch: () => void
}

/**
 * Helper to extract data from a TanStack Query that returns Result<T>.
 *
 * This bridges TanStack Query with the Result pattern by:
 * - Extracting data from successful Results
 * - Converting Result errors to proper error state
 * - Providing error categorization and messages
 *
 * @example
 * ```typescript
 * // In your component
 * const statsQuery = useQuery({
 *   queryKey: ['dashboard-stats'],
 *   queryFn: dashboardService.getStats,
 * })
 *
 * const stats = useQueryResult(statsQuery)
 *
 * if (stats.isLoading) return <Spinner />
 * if (stats.isError) {
 *   return <Alert variant="destructive">{stats.errorMessage}</Alert>
 * }
 * return <StatsDisplay stats={stats.data} />
 * ```
 */
export function useQueryResult<T, E = APIErrorResponse>(
  query: {
    data: Result<T, E> | undefined
    isLoading: boolean
    isPending: boolean
    refetch: () => void
  }
): QueryResultReturn<T, E> {
  const { data: result, isLoading, isPending, refetch } = query

  // Still loading or no result yet
  if (isLoading || isPending || !result) {
    return {
      data: undefined,
      error: undefined,
      isLoading: isLoading || isPending,
      isSuccess: false,
      isError: false,
      errorCategory: undefined,
      errorMessage: undefined,
      isRetryable: false,
      refetch,
    }
  }

  // Successful result
  if (result.success) {
    return {
      data: result.data,
      error: undefined,
      isLoading: false,
      isSuccess: true,
      isError: false,
      errorCategory: undefined,
      errorMessage: undefined,
      isRetryable: false,
      refetch,
    }
  }

  // Error result
  const errorCategory = categorizeAPIError(result.error)
  const errorMessage = getErrorMessage(result.error)
  const isRetryable = isRetryableError(result.error)

  return {
    data: undefined,
    error: result.error,
    isLoading: false,
    isSuccess: false,
    isError: true,
    errorCategory,
    errorMessage,
    isRetryable,
    refetch,
  }
}

/**
 * Options for useMutationResult hook.
 */
export type UseMutationResultOptions<TData, E = APIErrorResponse> = {
  /** Callback fired on successful result */
  onSuccess?: (data: TData) => void
  /** Callback fired on error result */
  onError?: (error: E) => void
  /** Callback fired when mutation settles (success or error) */
  onSettled?: () => void
}

/**
 * Return type from useMutationResult - enhances TanStack useMutation with Result pattern.
 */
export type MutationResultReturn<TData, TVariables, E = APIErrorResponse> = {
  /** The successful data from the last mutation */
  data: TData | undefined
  /** The error from the Result, undefined if success */
  error: E | undefined
  /** True while mutation is in progress */
  isPending: boolean
  /** True if the last mutation succeeded */
  isSuccess: boolean
  /** True if the Result contained an error */
  isError: boolean
  /** True if mutation has never been called */
  isIdle: boolean
  /** Error category for displaying appropriate UI */
  errorCategory: ReturnType<typeof categorizeAPIError> | undefined
  /** Human-readable error message */
  errorMessage: string | undefined
  /** Whether error is retryable (server errors, rate limits) */
  isRetryable: boolean
  /** Execute the mutation */
  mutate: (variables: TVariables) => void
  /** Execute the mutation and return a promise */
  mutateAsync: (variables: TVariables) => Promise<Result<TData, E>>
  /** Reset mutation state */
  reset: () => void
}

/**
 * Helper to extract data from a TanStack Mutation that returns Result<T>.
 *
 * This bridges TanStack useMutation with the Result pattern by:
 * - Extracting data from successful Results
 * - Converting Result errors to proper error state
 * - Providing error categorization and messages
 * - Wrapping mutate/mutateAsync with Result-aware callbacks
 *
 * @example
 * ```typescript
 * // Define the mutation
 * const deleteMutation = useMutation({
 *   mutationFn: (id: string) => modelsService.delete(id),
 * })
 *
 * // Wrap with useMutationResult for Result pattern support
 * const deleteModel = useMutationResult(deleteMutation, {
 *   onSuccess: () => {
 *     toast.success('Model deleted')
 *     queryClient.invalidateQueries(['models'])
 *   },
 *   onError: (error) => {
 *     toast.error(error.message)
 *   }
 * })
 *
 * // Usage in component
 * <Button
 *   onClick={() => deleteModel.mutate(modelId)}
 *   disabled={deleteModel.isPending}
 * >
 *   {deleteModel.isPending ? 'Deleting...' : 'Delete'}
 * </Button>
 *
 * {deleteModel.isError && (
 *   <Alert variant="destructive">{deleteModel.errorMessage}</Alert>
 * )}
 * ```
 */
export function useMutationResult<TData, TVariables, E = APIErrorResponse>(
  mutation: {
    data: Result<TData, E> | undefined
    isPending: boolean
    isSuccess: boolean
    isIdle: boolean
    mutate: (variables: TVariables) => void
    mutateAsync: (variables: TVariables) => Promise<Result<TData, E>>
    reset: () => void
  },
  options: UseMutationResultOptions<TData, E> = {}
): MutationResultReturn<TData, TVariables, E> {
  const { onSuccess, onError, onSettled } = options
  const prevDataRef = useRef<Result<TData, E> | undefined>(undefined)

  // Fire callbacks when mutation result changes
  useEffect(() => {
    const result = mutation.data
    if (!result || result === prevDataRef.current) return
    prevDataRef.current = result

    if (result.success) {
      onSuccess?.(result.data)
    } else {
      onError?.(result.error)
    }
    onSettled?.()
  }, [mutation.data, onSuccess, onError, onSettled])

  const result = mutation.data

  // Idle state - never been called
  if (mutation.isIdle || !result) {
    return {
      data: undefined,
      error: undefined,
      isPending: mutation.isPending,
      isSuccess: false,
      isError: false,
      isIdle: mutation.isIdle && !mutation.isPending,
      errorCategory: undefined,
      errorMessage: undefined,
      isRetryable: false,
      mutate: mutation.mutate,
      mutateAsync: mutation.mutateAsync,
      reset: mutation.reset,
    }
  }

  // Successful result
  if (result.success) {
    return {
      data: result.data,
      error: undefined,
      isPending: mutation.isPending,
      isSuccess: true,
      isError: false,
      isIdle: false,
      errorCategory: undefined,
      errorMessage: undefined,
      isRetryable: false,
      mutate: mutation.mutate,
      mutateAsync: mutation.mutateAsync,
      reset: mutation.reset,
    }
  }

  // Error result
  const errorCategory = categorizeAPIError(result.error)
  const errorMessage = getErrorMessage(result.error)
  const isRetryable = isRetryableError(result.error)

  return {
    data: undefined,
    error: result.error,
    isPending: mutation.isPending,
    isSuccess: false,
    isError: true,
    isIdle: false,
    errorCategory,
    errorMessage,
    isRetryable,
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    reset: mutation.reset,
  }
}
